/**
 * Formatação e conversões.
 *
 * As fórmulas vêm do traccar-web (Apache-2.0), para não divergir da semântica
 * do servidor: https://github.com/traccar/traccar-web
 *   src/common/util/converter.js
 *   src/common/util/formatter.js
 *
 * O Traccar guarda velocidade em NÓS e distância em METROS. Converter errado
 * aqui produz número plausível e errado, que é o pior tipo de bug num painel.
 *
 * ── Conferido contra a referência em 2026-10-15 ──────────────────────────
 *
 * IDÊNTICOS à `converter.js`/`formatter.js`, e devem continuar sendo — mudar
 * um destes é mudar o SIGNIFICADO do número, não a aparência dele:
 *
 *   round12         ≡ roundFloat          Number(v.toPrecision(12))
 *   kmh             ≡ speedFromKnots      × 1,852
 *   km              ≡ distanceFromMeters  × 0,001
 *   seta            ≡ formatCourse        (g + 22,5) % 360, corrige negativo
 *   statusBateria   ≡ getBatteryStatus    ≥70 · >30 · resto
 *
 * DIVERGEM de propósito, e cada uma tem um motivo:
 *
 *   velocidade   1 casa decimal; o traccar-web usa 2. Os MODELOS XLSX do
 *                próprio Traccar usam 1 (`%.1f km/h`), então divergimos da
 *                tela dele e batemos com a planilha dele — que é o documento
 *                que sai daqui para fora.
 *   distancia    metros abaixo de 1 km; o traccar-web sempre escreve km com 2
 *                casas. "0,02 km" para 23 m fez a tela dizer que o veículo não
 *                andou.
 *   percentual   arredonda; o traccar-web imprime o valor cru. Nível de
 *                bateria com decimal é precisão que o sensor não tem.
 *
 * NÃO TEMOS, e é escopo, não esquecimento: mi/nmi, mph/kn, pés e galões. O
 * produto é pt-BR e métrico; a unidade não é configurável, então não há
 * `unit` para passar adiante.
 */

export const nf = new Intl.NumberFormat("pt-BR");
export const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

// ── conversões (converter.js) ─────────────────────────────────────────────
const round12 = (v: number) => Number(v.toPrecision(12));

/** nós → km/h */
export const kmh = (knots: number) => round12(knots * 1.852);
/** metros → km */
export const km = (meters: number) => round12(meters * 0.001);

// ── formatação (formatter.js) ─────────────────────────────────────────────
const SETAS = ["↑", "↗", "→", "↘", "↓", "↙", "←", "↖"];

/** Curso em graus → seta. Mais legível que "247°" numa tabela densa. */
export function seta(graus: number): string {
  let v = (graus + 45 / 2) % 360;
  if (v < 0) v += 360;
  return SETAS[Math.floor(v / 45)];
}

export const temperatura = (v: number) => `${v.toFixed(1)} °C`;
export const voltagem = (v: number) => `${v.toFixed(2)} V`;
export const percentual = (v: number) => `${Math.round(v)}%`;

/** Faixas de bateria do traccar-web: ≥70 bom, >30 atenção, resto crítico. */
export function statusBateria(nivel: number): "bom" | "atencao" | "critico" {
  if (nivel >= 70) return "bom";
  if (nivel > 30) return "atencao";
  return "critico";
}

const dtCompleto = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
});
const dtHora = new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", minute: "2-digit" });

export function quando(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const hoje = new Date();
  const mesmoDia = d.toDateString() === hoje.toDateString();
  return mesmoDia ? dtHora.format(d) : dtCompleto.format(d);
}

/** "há 3 min" — o gestor quer saber se o dado é fresco, não o relógio exato. */
export function ha(iso: string | null | undefined): string {
  if (!iso) return "—";
  const seg = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seg < 60) return "agora";
  if (seg < 3600) return `há ${Math.floor(seg / 60)} min`;
  if (seg < 86400) return `há ${Math.floor(seg / 3600)} h`;
  return `há ${Math.floor(seg / 86400)} d`;
}

/** Percentual e dias de histórico são o mesmo número, invertido (docs/10). */
export const diasEfetivos = (pct: number, retencao = 45) =>
  pct <= 100 ? retencao : Math.round((retencao * 100) / pct);

/**
 * Distância em METROS para leitura.
 *
 * Abaixo de 1 km mostra metros: arredondar 23 m para "0 km" fez a tela dizer
 * que 300 veículos rodaram e, na linha de cada um, que rodaram zero.
 */
export function distancia(metros: number): string {
  if (!Number.isFinite(metros) || metros < 1) return "0 m";
  if (metros < 1000) return `${Math.round(metros)} m`;
  const k = metros / 1000;
  return `${k < 10 ? k.toFixed(1).replace(".", ",") : nf.format(Math.round(k))} km`;
}

/** Duração em ms → "2 h 14 min". O Traccar dá duração em milissegundos. */
/**
 * Velocidade, em km/h, legível.
 *
 * ⚠️ `Math.round` cru produzia `5 km/h` para 5,1 e `6495844 km/h` para um
 * valor absurdo — sem decimal e sem separador de milhar, que é o que fazia o
 * número parecer defeito de conversão. A conversão de nós sempre esteve certa;
 * a apresentação é que não dizia o que o número era.
 *
 * Uma casa decimal abaixo de 100 (onde ela informa) e nenhuma acima (onde só
 * polui), com separador de milhar — o mesmo critério do `distancia`.
 */
export function velocidade(kmPorHora: number): string {
  if (!Number.isFinite(kmPorHora) || kmPorHora <= 0) return "0 km/h";
  return kmPorHora < 100
    ? `${kmPorHora.toFixed(1).replace(".", ",")} km/h`
    : `${nf.format(Math.round(kmPorHora))} km/h`;
}

/**
 * Duração em `hh:mm`, para planilha e PDF.
 *
 * ⚠️ Diferente do `duracao`, que escreve "3 h 20 min" — bom de ler na tela e
 * ruim numa coluna, onde não alinha nem soma. Aqui as horas NÃO viram dias:
 * "124:30" é o que se espera de um totalizador de horímetro.
 */
export function horasMinutos(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "00:00";
  // ⚠️ TRUNCA, como o `formatNumericHours` da referência — e como o
  // `horasMotor` aqui ao lado. Arredondar os minutos primeiro fazia
  // 59 min 59 s virar "01:00": a mesma grandeza escrita de dois jeitos em
  // duas colunas da mesma planilha.
  const h = Math.floor(ms / 3600000);
  const min = Math.floor((ms % 3600000) / 60000);
  return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
}

/**
 * Horas de motor, em ms → "339 h 17 min".
 *
 * ⚠️ Horímetro NÃO vira dias, e é por isso que não usa o `duracao`. Ele
 * escrevia "14 d 3 h" para 339 horas de motor — e dias se leem como tempo
 * DECORRIDO, que é outra coisa: o veículo não ficou ligado quatorze dias
 * seguidos, ele acumulou 339 horas ao longo do período.
 *
 * É o `formatNumericHours` da referência, com os rótulos em português.
 */
export function horasMotor(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "0 h";
  const h = Math.floor(ms / 3600000);
  const min = Math.floor((ms % 3600000) / 60000);
  return min ? `${nf.format(h)} h ${min} min` : `${nf.format(h)} h`;
}

/**
 * Duração de um intervalo — viagem, parada, janela.
 *
 * ⚠️ Vira dias acima de 24 h, e por isso NÃO serve para horímetro: ali o
 * número é acumulado, não decorrido. Use `horasMotor`.
 */
export function duracao(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "—";
  const min = Math.round(ms / 60000);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const r = min % 60;
  if (h < 24) return r ? `${h} h ${r} min` : `${h} h`;
  return `${Math.floor(h / 24)} d ${h % 24} h`;
}

const dtDia = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit", month: "2-digit", year: "2-digit",
  hour: "2-digit", minute: "2-digit",
});
/** Data e hora completas — em relatório não dá para omitir o dia. */
export const dataHora = (iso: string | null | undefined) =>
  iso ? dtDia.format(new Date(iso)) : "—";

/** `Date` → valor de um `<input type="datetime-local">`, no fuso local. */
export function paraInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}` +
         `T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * Uma chave de dia (`2026-10-29`) escrita como `29/10/26`.
 *
 * ⚠️ A chave NÃO passa por `new Date("2026-10-29")` nem por `Intl`: ela já é
 * ano, mês e dia, e só precisa ser remontada na outra ordem. A forma com
 * `new Date` seria lida como meia-noite UTC e, no fuso de Brasília, cairia às
 * 21h do dia ANTERIOR — um relatório diário inteiro deslocado um dia, sem
 * erro nenhum na tela.
 */
export function dataDoDia(chave: string): string {
  const [a, m, d] = chave.split("-");
  return a && m && d ? `${d}/${m}/${a.slice(2)}` : chave;
}

/**
 * `Date` → chave de dia (`2026-10-29`) NO FUSO EM QUE O SERVIDOR CORTA O DIA.
 *
 * ⚠️ O `fuso` não é enfeite. Quem parte a janela do resumo diário em dias é o
 * Traccar, no fuso do tenant; o que volta de cada dia é a hora da primeira
 * posição dele. Traduzir esse instante com o fuso do NAVEGADOR funciona
 * enquanto os dois coincidem e quebra feio quando não: dois dias do servidor
 * caem no mesmo dia da tela, o mesmo veículo aparece duas vezes na mesma data
 * e o dia seguinte some.
 *
 * Sem `fuso` — o Traccar usa o da JVM do container e a API não o expõe — cai
 * no do navegador, que é o melhor palpite disponível.
 */
export function chaveDoDia(d: Date, fuso?: string): string {
  if (fuso) {
    try {
      // `formatToParts` em vez de uma chave de idioma que "por acaso" imprime
      // ISO: aqui o ano, o mês e o dia são pedidos pelo nome.
      const partes = new Intl.DateTimeFormat("en-CA", {
        timeZone: fuso, year: "numeric", month: "2-digit", day: "2-digit",
      }).formatToParts(d);
      const pegar = (t: string) => partes.find(x => x.type === t)?.value ?? "";
      const [a, m, dia] = [pegar("year"), pegar("month"), pegar("day")];
      if (a && m && dia) return `${a}-${m}-${dia}`;
    } catch {
      // Fuso inválido em `attributes` é campo livre mal preenchido, não motivo
      // para a tela inteira cair: segue com o do navegador.
    }
  }
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
